import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';

// PLREP-S1 — Paid Leave Report screen. Year picker + Export to Excel button.
// The .xlsx is generated server-side (ClosedXML, two sheets: "Paid Leave" +
// "Balances"). No inline preview — the file itself is the report.
export default function PaidLeaveReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  const handleExport = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const blob = await api.get(`/paid-leave-report/export?year=${selectedYear}`, {
        responseType: 'blob'
      });

      if (!blob || !(blob instanceof Blob)) {
        setError('No data received from server. Please try again.');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaidLeaveReport_${selectedYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Report exported for ${selectedYear}.`);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to export this report.');
      } else {
        setError('Failed to export report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Paid Leave Report
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Export an Excel workbook with every paid annual leave request for the selected year
        (one row per leave) plus each employee's balance as of the moment the file was
        generated. Filters and sorting are handled natively in Excel via AutoFilter on both
        sheets.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {yearOptions.map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </Stack>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Workbook contents:</Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Sheet 1 — Paid Leave</strong>: one row per paid annual leave request
          (Approved, Pending, Rejected), sorted by employee then start date. Columns:
          Employee, Email, Department, Start date, End date, Working days, Approval status,
          Submitted on, Approved on, Approver.<br />
          <strong>Sheet 2 — Balances</strong>: one row per employee. Columns: Employee,
          Email, Department, Entitled, Carried over, Used, Pending, Remaining, Available now.
          Numbers match the Annual Leave Balance Report for the same year.
        </Typography>
      </Box>
    </Paper>
  );
}
